import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Modal, Spinner } from "react-bootstrap";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";
import {
  useAccounts,
  AccountSelect,
  NoAccountsWarning,
  financeAuthPayload,
} from "../../components/finance/AccountSelect";
import "../../Styles/yellocalfcss/Liabilities.css";

/**
 * Loans
 *
 * Creating a loan DISBURSES it: the backend posts a journal (Dr Cash/Bank,
 * Cr Loans Payable), so the create form collects both accounts. A repayment
 * posts Dr Loans Payable (principal) + Dr Interest Expense (interest),
 * Cr Cash/Bank — hence the repayment modal collects the cash account and, when
 * interest is paid, the interest expense account. Only descriptive fields may
 * be edited after disbursement.
 *
 * Backend dependency: ApiConfig.loanList / loanCreate / loanUpdate /
 * loanRepayment.
 */
const Loans = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const { accounts, loading: accountsLoading } = useAccounts();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  const [showRepayModal, setShowRepayModal] = useState(false);
  const [repayLoan, setRepayLoan] = useState(null);
  const [repay, setRepay] = useState(blankRepay());
  const [repaying, setRepaying] = useState(false);

  // Amortization schedule modal
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleLoan, setScheduleLoan] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  function blankForm() {
    return {
      loan_name: "",
      loan_account_id: "",
      disbursement_account_id: "",
      principal_amount: "",
      interest_rate: "",
      loan_term_months: "",
      repayment_frequency: "monthly",
      loan_date: new Date().toISOString().slice(0, 10),
      loan_description: "",
    };
  }

  function blankRepay() {
    return {
      repayment_date: new Date().toISOString().slice(0, 10),
      principal_component: "",
      interest_component: "",
      payment_account_id: "",
      interest_expense_account_id: "",
    };
  }

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.loanList,
        JSON.stringify({ ...financeAuthPayload(), status: "", search: "" })
      );
      setRows(res?.result && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Loan list fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  const totals = rows.reduce(
    (acc, r) => {
      acc.principal += parseFloat(r.principal_amount) || 0;
      acc.outstanding += parseFloat(r.outstanding_principal) || 0;
      return acc;
    },
    { principal: 0, outstanding: 0 }
  );

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      loan_name: r.loan_name || "",
      loan_account_id: r.loan_account_id || "",
      disbursement_account_id: r.disbursement_account_id || "",
      principal_amount: r.principal_amount || "",
      interest_rate: r.interest_rate || "",
      loan_term_months: r.loan_term_months || "",
      repayment_frequency: r.repayment_frequency || "monthly",
      loan_date: r.loan_date ? String(r.loan_date).slice(0, 10) : "",
      loan_description: r.loan_description || "",
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveLoan = async () => {
    if (editing) {
      if (!form.loan_name) {
        toast.warning("Loan name is required.");
        return;
      }
      await submitLoan(ApiConfig.loanUpdate, {
        ...financeAuthPayload(),
        loan_id: editing.loan_id,
        loan_name: form.loan_name,
        loan_description: form.loan_description,
        loan_term_months: form.loan_term_months
          ? Number(form.loan_term_months)
          : null,
        repayment_frequency: form.repayment_frequency,
      });
      return;
    }

    if (
      !form.loan_name ||
      !form.principal_amount ||
      !form.loan_account_id ||
      !form.disbursement_account_id ||
      !form.loan_date
    ) {
      toast.warning(
        "Loan name, principal, disbursement date, loan account and disbursement account are all required."
      );
      return;
    }
    await submitLoan(ApiConfig.loanCreate, {
      ...financeAuthPayload(),
      loan_name: form.loan_name,
      loan_account_id: Number(form.loan_account_id),
      disbursement_account_id: Number(form.disbursement_account_id),
      principal_amount: Number(form.principal_amount),
      interest_rate: form.interest_rate ? Number(form.interest_rate) : 0,
      loan_term_months: form.loan_term_months
        ? Number(form.loan_term_months)
        : null,
      repayment_frequency: form.repayment_frequency,
      loan_date: form.loan_date,
      loan_description: form.loan_description,
    });
  };

  const submitLoan = async (url, payload) => {
    setSaving(true);
    try {
      const res = await simplePostCall(url, JSON.stringify(payload));
      if (res?.result) {
        toast.success(editing ? "Loan updated." : "Loan disbursed.");
        setShowModal(false);
        fetchLoans();
      } else {
        toast.error(res?.message || "Save failed.");
      }
    } catch (err) {
      toast.error("Error saving loan.");
    } finally {
      setSaving(false);
    }
  };

  const openRepayModal = (loan) => {
    setRepayLoan(loan);
    setRepay(blankRepay());
    setShowRepayModal(true);
  };

  const submitRepayment = async () => {
    const principal = Number(repay.principal_component) || 0;
    const interest = Number(repay.interest_component) || 0;
    if (principal + interest <= 0) {
      toast.warning("Enter at least one of principal or interest.");
      return;
    }
    if (!repay.payment_account_id) {
      toast.warning("Select the cash / bank account the repayment is made from.");
      return;
    }
    if (interest > 0 && !repay.interest_expense_account_id) {
      toast.warning("Select an interest expense account for the interest portion.");
      return;
    }
    setRepaying(true);
    try {
      const payload = {
        ...financeAuthPayload(),
        loan_id: repayLoan.loan_id,
        repayment_date: repay.repayment_date,
        principal_component: principal,
        interest_component: interest,
        payment_account_id: Number(repay.payment_account_id),
      };
      if (interest > 0) {
        payload.interest_expense_account_id = Number(
          repay.interest_expense_account_id
        );
      }
      const res = await simplePostCall(
        ApiConfig.loanRepayment,
        JSON.stringify(payload)
      );
      if (res?.result) {
        toast.success(
          res.data?.loan_status === "closed"
            ? "Repayment recorded — loan fully paid off."
            : "Repayment recorded."
        );
        setShowRepayModal(false);
        fetchLoans();
      } else {
        toast.error(res?.message || "Failed to record repayment.");
      }
    } catch (err) {
      toast.error("Error recording repayment.");
    } finally {
      setRepaying(false);
    }
  };

  const openSchedule = async (loan) => {
    setScheduleLoan(loan);
    setSchedule([]);
    setShowSchedule(true);
    setScheduleLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.loanSchedule,
        JSON.stringify({ ...financeAuthPayload(), loan_id: loan.loan_id })
      );
      if (res?.result) {
        setSchedule(res.data?.schedule || []);
      } else {
        toast.error(res?.message || "No schedule available.");
      }
    } catch (err) {
      toast.error("Failed to load repayment schedule.");
    } finally {
      setScheduleLoading(false);
    }
  };

  const statusLabel = (s) =>
    ({ active: "Active", closed: "Closed" }[
      String(s || "active").toLowerCase()
    ] || "Active");

  const noAccounts = !accountsLoading && accounts.length === 0;

  return (
    <div className="liabilities-page">
      <ToastContainer />
      <Container fluid>
        <div className="special-header panel-white-all d-flex justify-content-between align-items-center">
          <h4 className="page-title">Loans</h4>
          <Button
            onClick={openCreate}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            + Add Loan
          </Button>
        </div>

        <div className="lib-summary-row">
          <div className="lib-tile lib-tile-total">
            <span className="lib-tile-label">Total Principal</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.principal)}
            </span>
          </div>
          <div className="lib-tile lib-tile-outstanding">
            <span className="lib-tile-label">Outstanding Principal</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.outstanding)}
            </span>
          </div>
        </div>

        <div className="lib-table-wrap">
          <table className="lib-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Loan</th>
                <th>Principal</th>
                <th>Interest %</th>
                <th>Outstanding</th>
                <th>Disbursed</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <ScaleLoader color="#F4BB44" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    No loans recorded.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const closed =
                    String(r.loan_status || "active").toLowerCase() === "closed";
                  return (
                    <tr key={r.loan_id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <div className="fw-semibold">{r.loan_name}</div>
                        {r.loan_description && (
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {r.loan_description}
                          </div>
                        )}
                      </td>
                      <td>
                        {customer_currency} {formatNumber(r.principal_amount)}
                      </td>
                      <td>{r.interest_rate ? `${r.interest_rate}%` : "—"}</td>
                      <td className="fw-bold">
                        {customer_currency}{" "}
                        {formatNumber(r.outstanding_principal)}
                      </td>
                      <td>
                        {r.loan_date
                          ? new Date(r.loan_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>{statusLabel(r.loan_status)}</td>
                      <td className="text-center">
                        {!closed && (
                          <Button
                            size="sm"
                            className="me-1"
                            onClick={() => openRepayModal(r)}
                            style={{
                              backgroundColor: "var(--color-primary)",
                              border: "none",
                            }}
                          >
                            Record Repayment
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-1"
                          onClick={() => openSchedule(r)}
                        >
                          Schedule
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openEdit(r)}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Container>

      {/* Create / edit loan modal */}
      <Modal
        show={showModal}
        onHide={() => !saving && setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Edit Loan" : "Disburse Loan"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!editing && noAccounts && <NoAccountsWarning />}
          {editing && (
            <div className="fin-inline-warning">
              This loan is already disbursed. Only the name and description can be
              changed — the principal and its ledger accounts are locked.
            </div>
          )}
          <div className="row g-3">
            <div className="col-md-12">
              <Form.Group>
                <Form.Label>
                  Loan Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="loan_name"
                  value={form.loan_name}
                  onChange={handleFormChange}
                  placeholder="e.g. School bus financing 2024 — KCB Bank"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Loan Account (credit) <span className="text-danger">*</span>
                </Form.Label>
                <AccountSelect
                  accounts={accounts}
                  name="loan_account_id"
                  types={["liability"]}
                  value={form.loan_account_id}
                  onChange={handleFormChange}
                  disabled={!!editing}
                  placeholder="Select loans payable account"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Disbursed To (Cash / Bank){" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <AccountSelect
                  accounts={accounts}
                  name="disbursement_account_id"
                  types={["asset"]}
                  value={form.disbursement_account_id}
                  onChange={handleFormChange}
                  disabled={!!editing}
                  placeholder="Select cash / bank account"
                />
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>
                  Principal <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="principal_amount"
                  value={form.principal_amount}
                  onChange={handleFormChange}
                  disabled={!!editing}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Interest Rate (%)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="interest_rate"
                  value={form.interest_rate}
                  onChange={handleFormChange}
                  disabled={!!editing}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>
                  Disbursement Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="loan_date"
                  value={form.loan_date}
                  onChange={handleFormChange}
                  disabled={!!editing}
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Term (months)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  name="loan_term_months"
                  value={form.loan_term_months}
                  onChange={handleFormChange}
                  placeholder="e.g. 24 — needed for the repayment schedule"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Repayment Frequency</Form.Label>
                <Form.Select
                  name="repayment_frequency"
                  value={form.repayment_frequency}
                  onChange={handleFormChange}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-12">
              <Form.Group>
                <Form.Label>Description / Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="loan_description"
                  value={form.loan_description}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={saveLoan}
            disabled={saving || (!editing && noAccounts)}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {saving ? (
              <Spinner size="sm" />
            ) : editing ? (
              "Save Changes"
            ) : (
              "Disburse Loan"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Repayment modal */}
      <Modal
        show={showRepayModal}
        onHide={() => !repaying && setShowRepayModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Record Repayment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {repayLoan && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
              <div>
                <b>{repayLoan.loan_name}</b>
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Outstanding {customer_currency}{" "}
                {formatNumber(repayLoan.outstanding_principal)}
              </div>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Repayment Date</Form.Label>
            <Form.Control
              type="date"
              value={repay.repayment_date}
              onChange={(e) =>
                setRepay({ ...repay, repayment_date: e.target.value })
              }
            />
          </Form.Group>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Principal</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={repay.principal_component}
                  onChange={(e) =>
                    setRepay({ ...repay, principal_component: e.target.value })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Interest</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={repay.interest_component}
                  onChange={(e) =>
                    setRepay({ ...repay, interest_component: e.target.value })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mt-3">
            <Form.Label>
              Paid From (Cash / Bank) <span className="text-danger">*</span>
            </Form.Label>
            <AccountSelect
              accounts={accounts}
              types={["asset"]}
              value={repay.payment_account_id}
              onChange={(e) =>
                setRepay({ ...repay, payment_account_id: e.target.value })
              }
              placeholder="Select cash / bank account"
            />
          </Form.Group>
          {Number(repay.interest_component) > 0 && (
            <Form.Group className="mt-3">
              <Form.Label>
                Interest Expense Account <span className="text-danger">*</span>
              </Form.Label>
              <AccountSelect
                accounts={accounts}
                types={["expense"]}
                value={repay.interest_expense_account_id}
                onChange={(e) =>
                  setRepay({
                    ...repay,
                    interest_expense_account_id: e.target.value,
                  })
                }
                placeholder="Select interest expense account"
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRepayModal(false)}
            disabled={repaying}
          >
            Cancel
          </Button>
          <Button
            onClick={submitRepayment}
            disabled={repaying}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {repaying ? <Spinner size="sm" /> : "Record Repayment"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Amortization schedule modal */}
      <Modal
        show={showSchedule}
        onHide={() => setShowSchedule(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Repayment Schedule{scheduleLoan ? ` — ${scheduleLoan.loan_name}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scheduleLoading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : schedule.length === 0 ? (
            <div className="text-muted text-center py-3">
              No schedule available. Set the loan's term (months) and repayment
              frequency, then reopen.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="lib-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Due Date</th>
                    <th>Principal</th>
                    <th>Interest</th>
                    <th>Total</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((p, i) => (
                    <tr key={p.period || i}>
                      <td>{p.period || i + 1}</td>
                      <td>
                        {p.due_date
                          ? new Date(p.due_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        {customer_currency}{" "}
                        {formatNumber(p.principal_component)}
                      </td>
                      <td>
                        {customer_currency} {formatNumber(p.interest_component)}
                      </td>
                      <td className="fw-semibold">
                        {customer_currency} {formatNumber(p.total_payment)}
                      </td>
                      <td>
                        {customer_currency} {formatNumber(p.remaining_balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSchedule(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Loans;
